"use client";

import React, { useEffect, useRef, useState } from "react";
import HouseCompetitionForDesktop from "./houseCompetitionForDesktop";
import HouseCompetitionForMobile from "./houseCompetitionForMobile";
import {
  getCompetitionEndDate,
  getHouseAmeneties,
  getHouseMainInformation,
  getHousePrizeDetails,
} from "./house-service";
import { CountdownTimerData, HousePrizeBasicDetailsData, HouseContextDetails, HouseAmenetiesData } from "./types";
import "./houseCompetition.css";
import { HouseCompetitionData } from "./contextProvider";
import "react-loading-skeleton/dist/skeleton.css";
import Modal from "../../../components/Modal/modal";
import { useAppDispatch, useAppSelector } from "@/app/redux/hooks";
import { publishModalVisibility } from "@/app/redux/features/modal/modalSlice";
import { ToastProvider } from "@/app/components/ToastNotification/toastProvider";
import { getCurrencies } from "@/app/common-service";
import { AppCurrencies } from "@/app/common";
import BotbPassDrawer from "@/app/(general-header)/botb-pass-drawer/BotbPassDrawer";
import { useCompetition } from "@/app/redux/features/categories/selectors";
import CustomHeaderV3 from "@/app/(custom-header)/header/customHeaderV3";
import DeviceProvider from "@/app/redux/features/device/DeviceProvider";
import { useIsMobile } from "../../../redux/features/device/selectors";
import dayjs from "dayjs";
import { HouseMainInformation } from "./types";

const TWO_WEEKS = 1000 * 60 * 60 * 24 * 7 * 2;
const ONE_WEEK = 1000 * 60 * 60 * 24 * 7;
const THREE_DAYS = 1000 * 60 * 60 * 24 * 3;
const TWO_DAYS = 1000 * 60 * 60 * 24 * 2;
const TODAY = 1000 * 60 * 60 * 24;

const HouseCompetition = () => {
  const houseCompetition = useCompetition({
    CompetitionTypePrefix: "HC",
  });
  const isMobile = useIsMobile();
  const [windowInnerWidth, setWindowInnerWidth] = useState<number>(window.innerWidth);
  const [homeSpecsImage, setHomeSpecsImage] = useState<string>("");
  const [contextData, setContextData] = useState<HouseContextDetails>({
    activePriceWithoutCurrency: "",
    amenitiesPopupText: "",
    amentiesImages: [],
    basePriceWithCurrency: "",
    basePriceWithoutCurrency: "",
    cashAlternativePopupText: "",
    cashAlternativeText: "",
    competitionId: 0,
    competitionState: "over14Weeks",
    currencySymbol: "",
    detailsText: "",
    discountPercentage: 0,
    hideCashAlternative: false,
    isHousePrizeOnly: false,
    isPrizeAvailable: false,
    overrideDiscountValue: 0,
    prizeId: 0,
    prizeSubtitle: "",
    prizeTags: [],
    prizeTitle: "",
    shouldShowSubscriptionDrawer: false,
    socialReviews: "",
    houseValue: "",
    monthlyRental: "",
    homeSpecsImage,
    overridePrizeTitleSuffix: "",
    setHomeSpecsImage,
    setContextData: () => {},
  });
  const [competitionEndDate, setCompetitionEndDate] = useState<Date>();
  const isModalOpen = useAppSelector(state => state.modal.isOpen);
  const modalData = useAppSelector(state => state.modal.data);
  const modalOverlay = useAppSelector(state => state.modal.withOverlay);
  const modalClassNames = useAppSelector(state => state.modal.modalClassNames);
  const modalStyles = useAppSelector(state => state.modal.modalStyles);
  const dispatch = useAppDispatch();
  const selectedCurrencyCode = useRef("GBP");

  useEffect(() => {
    if (houseCompetition.CompetitionId && houseCompetition.FeaturedPrizeId && houseCompetition.Title) {
      getCurrencyList();
    }
  }, [houseCompetition]);

  useEffect(() => {
    getCountdownTimerData();
    renderRespectiveLayout();
    window.addEventListener("resize", renderRespectiveLayout);
    return () => window.removeEventListener("resize", renderRespectiveLayout);
  }, []);

  const getCurrencyList = async () => {
    try {
      const response = await getCurrencies();
      const data = (await response.json()) as AppCurrencies;
      selectedCurrencyCode.current = data.selectedCurrency?.CurrencyCode || "GBP";
    } catch (err) {
      console.error(`Error while retrieving currencies - ${err}`);
    } finally {
      getPrizeBasicDetails();
      getInformation(houseCompetition.FeaturedPrizeId);
    }
  };

  const getInformation = async (prizeId: number) => {
    try {
      const response = await getHouseMainInformation(prizeId);
      const data = (await response.json()) as Awaited<Promise<HouseMainInformation>>;
      const domParser = new DOMParser();
      const parsedHTML = domParser.parseFromString(data.HouseMainInformation, "text/html");
      setContextData(prevState => ({
        ...prevState,
        houseValue: parsedHTML.getElementById("house_value")?.textContent || "",
        monthlyRental: parsedHTML.getElementById("monthly_rental")?.textContent || "",
      }));
    } catch (err) {
      console.error(`Error while retrieving house prize information - ${err}`);
    }
  };

  const getPrizeBasicDetails = async () => {
    const [prizeBasicDataResponse, amenitiesDataResponse] = await Promise.all([
      getHousePrizeDetails(houseCompetition.FeaturedPrizeId, selectedCurrencyCode.current),
      getHouseAmeneties(houseCompetition.FeaturedPrizeId),
    ]);
    const prizeData = (await prizeBasicDataResponse.json()) as HousePrizeBasicDetailsData;
    const amentiesData = (await amenitiesDataResponse.json()) as HouseAmenetiesData;
    const shouldShowSubscriptionDrawer = prizeData.ShouldShowSubscriptionDrawer;
    let competitionState: HouseContextDetails["competitionState"] = "over14Weeks";
    const endDateInMs = dayjs(houseCompetition.EndDate).diff(dayjs(), "milliseconds");
    if (endDateInMs > TWO_WEEKS) {
      competitionState = "over14Weeks";
    } else if (endDateInMs > ONE_WEEK && endDateInMs <= TWO_WEEKS) {
      competitionState = "nextWeek";
    } else if (endDateInMs <= ONE_WEEK && endDateInMs > THREE_DAYS) {
      competitionState = "thisWeek";
    } else if (endDateInMs < THREE_DAYS && endDateInMs > TWO_DAYS) {
      competitionState = "last3Days";
    } else if (endDateInMs < TWO_DAYS && endDateInMs > TODAY) {
      competitionState = "last2Days";
    } else if (endDateInMs <= TODAY) {
      competitionState = "today";
    }
    setContextData(prevState => ({
      ...prevState,
      competitionId: houseCompetition.CompetitionId,
      prizeId: houseCompetition.FeaturedPrizeId,
      basePriceWithoutCurrency: prizeData?.TicketBasePriceWithoutCurrency,
      currencySymbol: prizeData?.CurrencySymbol,
      cashAlternativePopupText: prizeData?.HouseCashAlternativeData,
      cashAlternativeText: prizeData?.HouseCashAlternativeText,
      detailsText: prizeData?.DetailsText,
      prizeSubtitle: prizeData?.HousePrizeSubtitle,
      activePriceWithoutCurrency: prizeData?.TicketActivePriceWithoutCurrency,
      amenitiesPopupText: amentiesData?.HouseAmenities?.PopupText,
      amentiesImages: amentiesData?.HouseAmenities?.AmenityImages,
      prizeTitle: houseCompetition.Title,
      isPrizeAvailable: prizeData?.PrizeAvailable,
      discountPercentage: prizeData?.DiscountPercentValue,
      overrideDiscountValue: Number(prizeData?.OverrideDiscountPercentValue),
      basePriceWithCurrency: prizeData?.TicketBasePriceWithCurrency,
      socialReviews: prizeData?.OverlayContent,
      prizeTags: prizeData?.PrizeTags,
      shouldShowSubscriptionDrawer: shouldShowSubscriptionDrawer,
      hideCashAlternative: prizeData?.HideCashAlternative,
      isHousePrizeOnly: prizeData?.IsHousePrizeOnly,
      competitionState: competitionState,
      overridePrizeTitleSuffix: prizeData?.OverridePrizeTitleSuffix,
    }));
  };
  const renderRespectiveLayout = () => {
    setWindowInnerWidth(window.innerWidth);
  };

  const getCountdownTimerData = async () => {
    const response = await getCompetitionEndDate();
    const { CompetitionEndDate } = (await response.json()) as CountdownTimerData;
    setCompetitionEndDate(new Date(CompetitionEndDate));
  };

  return (
    <DeviceProvider>
      <HouseCompetitionData.Provider value={{ ...contextData, setContextData }}>
        <CustomHeaderV3 />
        <article className="block">
          {!isMobile ? <HouseCompetitionForDesktop /> : <HouseCompetitionForMobile />}
        </article>
        {contextData && <BotbPassDrawer shouldShowSubscriptionDrawer={contextData.shouldShowSubscriptionDrawer} />}
        <Modal
          withOverlay={modalOverlay}
          closeModal={event => {
            event.stopPropagation();
            dispatch(publishModalVisibility({ isOpen: false }));
          }}
          isOpen={isModalOpen}
          body={modalData}
          title=""
          modalClassNames={modalClassNames}
          customStyles={modalStyles}
        />
        <ToastProvider />
      </HouseCompetitionData.Provider>
    </DeviceProvider>
  );
};

export default HouseCompetition;
